import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, Card, Field, Input, PrimaryButton } from "@/components/admin/AdminUI";

export default function AdminPageSeo() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["admin-page-seo"],
    queryFn: async () => (await api.get("/admin/page-seo")).data,
  });
  const [form, setForm] = useState({});

  useEffect(() => {
    if (data.length) {
      const initial = {};
      data.forEach((p) => {
        initial[p.path] = {
          title: p.title || "",
          description: p.description || "",
          og_image_url: p.og_image_url || "",
          noindex: Boolean(p.noindex),
        };
      });
      setForm(initial);
    }
  }, [data]);

  async function save(path) {
    try {
      await api.put("/admin/page-seo", { path, ...form[path] });
      toast.success(`Saved SEO for ${path}`);
      qc.invalidateQueries({ queryKey: ["admin-page-seo"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  }

  function set(path, key, value) {
    setForm((f) => ({ ...f, [path]: { ...f[path], [key]: value } }));
  }

  return (
    <div data-testid="admin-page-seo">
      <PageHeader
        title="SEO — Static pages"
        subtitle="Title and meta description shown on Google and social previews. Per-item SEO for brands and clothing items is edited on each item's own page."
      />
      <div className="space-y-4">
        {data.map((p) => (
          <Card key={p.path}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-semibold">{p.label}</div>
                <div className="text-xs text-[#6C757D] font-mono">{p.path}</div>
              </div>
              <PrimaryButton
                onClick={() => save(p.path)}
                data-testid={`save-seo-${p.path.replace(/\//g, "_") || "home"}`}
              >
                Save
              </PrimaryButton>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="SEO title" hint="55–60 chars ideally">
                <Input
                  value={form[p.path]?.title || ""}
                  onChange={(e) => set(p.path, "title", e.target.value)}
                  placeholder="e.g. Sustainable Clothing — Sustainably Curated"
                  maxLength={120}
                  data-testid={`seo-title-${p.path.replace(/\//g, "_") || "home"}`}
                />
              </Field>
              <Field label="SEO description" hint="155–160 chars ideally">
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E9ECEF] rounded-md text-sm"
                  value={form[p.path]?.description || ""}
                  onChange={(e) => set(p.path, "description", e.target.value)}
                  maxLength={300}
                  data-testid={`seo-desc-${p.path.replace(/\//g, "_") || "home"}`}
                />
              </Field>
              <Field label="Custom OG image URL" hint="1200×630 ideally. Leave empty to fall back to site hero image.">
                <Input
                  value={form[p.path]?.og_image_url || ""}
                  onChange={(e) => set(p.path, "og_image_url", e.target.value)}
                  placeholder="https://res.cloudinary.com/.../og-cover.png"
                  maxLength={500}
                  data-testid={`seo-og-${p.path.replace(/\//g, "_") || "home"}`}
                />
              </Field>
              <Field label="Indexing">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={Boolean(form[p.path]?.noindex)}
                    onChange={(e) => set(p.path, "noindex", e.target.checked)}
                    data-testid={`seo-noindex-${p.path.replace(/\//g, "_") || "home"}`}
                  />
                  <span>Hide from search engines (noindex, nofollow)</span>
                </label>
              </Field>
            </div>
            <p className="text-[11px] text-[#6C757D] mt-2">
              URL: <code>{p.path}</code> (automatic — not editable)
            </p>
          </Card>
        ))}
      </div>
    </div>
  );
}
